import { useState, useCallback, useRef, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { X, ChevronLeft, ChevronRight } from 'lucide-react';

/* ── Images ── */
const AGING_IMG = 'https://customer-assets.emergentagent.com/job_5f4a6145-03d0-4dbd-a839-b5c64804a322/artifacts/7e9f0je3_Untitled%20design%20%2815%29.png';
const YOUNG_IMG = 'https://customer-assets.emergentagent.com/job_5f4a6145-03d0-4dbd-a839-b5c64804a322/artifacts/4hs0vnq8_Untitled%20design%20%2814%29.png';

/* ── 5 Zone groups (visible aging) ── */
const ZONE_SPOTS = [
  {
    id: 'upper_face', x: 45, y: 20,
    labelKey: 'aging.z_upper_face',
    subs: [
      { labelKey: 'aging.z_upper_sub1', descKey: 'aging.z_upper_sub1_desc' },
      { labelKey: 'aging.z_upper_sub2', descKey: 'aging.z_upper_sub2_desc' },
    ],
  },
  {
    id: 'eye_region', x: 40, y: 35,
    labelKey: 'aging.z_eye',
    subs: [
      { labelKey: 'aging.z_eye_sub1', descKey: 'aging.z_eye_sub1_desc' },
      { labelKey: 'aging.z_eye_sub2', descKey: 'aging.z_eye_sub2_desc' },
    ],
  },
  {
    id: 'mid_face', x: 43, y: 45,
    labelKey: 'aging.z_mid',
    subs: [
      { labelKey: 'aging.z_mid_sub1', descKey: 'aging.z_mid_sub1_desc' },
      { labelKey: 'aging.z_mid_sub2', descKey: 'aging.z_mid_sub2_desc' },
    ],
  },
  {
    id: 'lower_face', x: 42, y: 57,
    labelKey: 'aging.z_lower',
    subs: [
      { labelKey: 'aging.z_lower_sub1', descKey: 'aging.z_lower_sub1_desc' },
      { labelKey: 'aging.z_lower_sub2', descKey: 'aging.z_lower_sub2_desc' },
    ],
  },
  {
    id: 'neck', x: 48, y: 76,
    labelKey: 'aging.z_neck',
    subs: [
      { labelKey: 'aging.z_neck_sub1', descKey: 'aging.z_neck_sub1_desc' },
      { labelKey: 'aging.z_neck_sub2', descKey: 'aging.z_neck_sub2_desc' },
    ],
  },
];

/* ── 5 Inner aging markers ── */
const INNER_SPOTS = [
  { id: 'inflammation',   x: 73, y: 14, labelKey: 'aging.i_inflammation',   descKey: 'aging.i_inflammation_desc',   relatedAreas: ['upper_face', 'mid_face'] },
  { id: 'sleep_recovery', x: 79, y: 24, labelKey: 'aging.i_sleep_recovery', descKey: 'aging.i_sleep_recovery_desc', relatedAreas: ['eye_region'] },
  { id: 'stress_nerve',   x: 71, y: 34, labelKey: 'aging.i_stress_nerve',   descKey: 'aging.i_stress_nerve_desc',   relatedAreas: ['upper_face', 'lower_face'] },
  { id: 'metabolic',      x: 77, y: 44, labelKey: 'aging.i_metabolic',      descKey: 'aging.i_metabolic_desc',      relatedAreas: ['mid_face', 'lower_face'] },
  { id: 'oxidative',      x: 73, y: 54, labelKey: 'aging.i_oxidative',      descKey: 'aging.i_oxidative_desc',      relatedAreas: ['neck', 'eye_region'] },
  { id: 'hormonal',       x: 79, y: 64, labelKey: 'aging.i_hormonal',       descKey: 'aging.i_hormonal_desc',       relatedAreas: ['mid_face', 'neck'] },
  { id: 'mitochondrial',  x: 71, y: 74, labelKey: 'aging.i_mitochondrial',  descKey: 'aging.i_mitochondrial_desc',  relatedAreas: ['eye_region', 'lower_face'] },
  { id: 'cognitive',      x: 77, y: 84, labelKey: 'aging.i_cognitive',      descKey: 'aging.i_cognitive_desc',      relatedAreas: ['upper_face', 'eye_region'] },
  { id: 'immune',         x: 73, y: 94, labelKey: 'aging.i_immune',         descKey: 'aging.i_immune_desc',         relatedAreas: ['mid_face', 'neck'] },
];

/* ── Hook: detect mobile ── */
const useIsMobile = (bp = 640) => {
  const [m, setM] = useState(false);
  useEffect(() => {
    const mq = window.matchMedia(`(max-width: ${bp}px)`);
    setM(mq.matches);
    const h = (e) => setM(e.matches);
    mq.addEventListener('change', h);
    return () => mq.removeEventListener('change', h);
  }, [bp]);
  return m;
};

/* ── Glow on face ── */
const GlowRing = ({ x, y, warm, isMobile }) => {
  // Warm (visible aging): single outlined ring as before
  if (warm) {
    const sz = isMobile ? 36 : 42;
    return (
      <div className="absolute pointer-events-none" style={{ left: `${x}%`, top: `${y}%`, transform: 'translate(-50%,-50%)', zIndex: 8, animation: 'glowAppear 0.4s ease-out forwards' }}>
        <span className="block rounded-full" style={{
          width: sz, height: sz,
          backgroundColor: 'rgba(214,168,74,0.12)',
          border: '2px solid rgba(214,168,74,0.5)',
          boxShadow: '0 0 20px rgba(214,168,74,0.3), 0 0 40px rgba(214,168,74,0.1)',
          animation: 'pulseGlow 2s ease-in-out infinite',
        }} />
      </div>
    );
  }

  // Inner aging: soft diffused breathing halos, no borders
  const base = isMobile ? 32 : 38;
  return (
    <div className="absolute pointer-events-none" style={{ left: `${x}%`, top: `${y}%`, transform: 'translate(-50%,-50%)', zIndex: 8, animation: 'glowAppear 0.4s ease-out forwards' }}>
      {/* Outer halo — largest, faintest, slowest */}
      <span className="absolute rounded-full" style={{
        width: base * 1.8, height: base * 1.8,
        top: '50%', left: '50%', transform: 'translate(-50%,-50%)',
        backgroundColor: 'rgba(96,165,250,0.06)',
        boxShadow: '0 0 30px rgba(96,165,250,0.12)',
        animation: 'breathe 3.2s ease-in-out infinite',
      }} />
      {/* Mid halo */}
      <span className="absolute rounded-full" style={{
        width: base * 1.2, height: base * 1.2,
        top: '50%', left: '50%', transform: 'translate(-50%,-50%)',
        backgroundColor: 'rgba(96,165,250,0.10)',
        boxShadow: '0 0 20px rgba(96,165,250,0.15)',
        animation: 'breathe 2.4s ease-in-out 0.4s infinite',
      }} />
      {/* Core halo — smallest, brightest, fastest */}
      <span className="absolute rounded-full" style={{
        width: base * 0.7, height: base * 0.7,
        top: '50%', left: '50%', transform: 'translate(-50%,-50%)',
        backgroundColor: 'rgba(96,165,250,0.18)',
        boxShadow: '0 0 12px rgba(96,165,250,0.25)',
        animation: 'breathe 1.8s ease-in-out 0.8s infinite',
      }} />
    </div>
  );
};

/* ── Tab bar ── */
const TabBar = ({ items, activeId, onSelect, warm, t, isMobile }) => {
  const useScroll = isMobile && items.length > 5;
  return (
    <div
      data-testid="aging-tab-bar"
      className={`flex gap-2 pb-1 pt-4 px-1 no-scrollbar ${useScroll ? 'overflow-x-auto' : 'justify-center flex-wrap'}`}
      style={{ scrollbarWidth: 'none' }}
    >
      {items.map(item => {
        const isActive = activeId === item.id;
        return (
          <button
            key={item.id}
            data-testid={`aging-tab-${item.id}`}
            onClick={() => onSelect(item.id)}
            className={`shrink-0 py-2 rounded-full font-medium tracking-wide transition-all duration-300 whitespace-nowrap ${useScroll ? 'px-3 text-[10px]' : 'px-4 text-[11px] sm:text-xs'}`}
            style={{
            fontFamily: 'Inter,sans-serif',
            color: isActive
              ? (warm ? '#0a1220' : '#0a1220')
              : (warm ? 'rgba(214,168,74,0.75)' : 'rgba(96,165,250,0.75)'),
            backgroundColor: isActive
              ? (warm ? '#d6a84a' : '#60a5fa')
              : (warm ? 'rgba(214,168,74,0.08)' : 'rgba(96,165,250,0.08)'),
            border: `1px solid ${isActive
              ? 'transparent'
              : (warm ? 'rgba(214,168,74,0.2)' : 'rgba(96,165,250,0.2)')}`,
            boxShadow: isActive
              ? (warm ? '0 4px 14px rgba(214,168,74,0.3)' : '0 4px 14px rgba(96,165,250,0.3)')
              : 'none',
          }}
        >
          {t(item.labelKey)}
        </button>
      );
    })}
  </div>
  );
};

/* ── Zone card content (2 sub-items) ── */
const ZoneCardContent = ({ zoneLabel, subs, warm, onClose, t }) => (
  <div
    className="rounded-2xl border p-4 sm:p-5 backdrop-blur-xl"
    style={{
      backgroundColor: warm ? 'rgba(20,15,8,0.92)' : 'rgba(8,15,30,0.92)',
      borderColor: warm ? 'rgba(214,168,74,0.3)' : 'rgba(96,165,250,0.3)',
      boxShadow: warm
        ? '0 8px 32px rgba(214,168,74,0.15), 0 0 0 1px rgba(214,168,74,0.08)'
        : '0 8px 32px rgba(96,165,250,0.15), 0 0 0 1px rgba(96,165,250,0.08)',
    }}
  >
    <div className="flex items-start justify-between gap-2 mb-3">
      <h4 className="text-sm font-semibold tracking-wide" style={{ color: warm ? '#d6a84a' : '#60a5fa', fontFamily: 'Inter,sans-serif' }}>{zoneLabel}</h4>
      {onClose && (
        <button onClick={onClose} className="shrink-0 rounded-full p-0.5" style={{ color: warm ? 'rgba(214,168,74,0.6)' : 'rgba(96,165,250,0.6)' }}>
          <X size={14} />
        </button>
      )}
    </div>
    <div className="space-y-3">
      {subs.map((sub, i) => (
        <div key={i}>
          <p className="text-xs font-medium mb-0.5" style={{ color: warm ? 'rgba(214,168,74,0.85)' : 'rgba(96,165,250,0.85)', fontFamily: 'Inter,sans-serif' }}>{t(sub.labelKey)}</p>
          <p className="text-[11px] leading-relaxed" style={{ color: 'rgba(245,241,232,0.72)', fontFamily: 'Inter,sans-serif' }}>{t(sub.descKey)}</p>
        </div>
      ))}
    </div>
  </div>
);

/* ── Inner aging single card ── */
const InnerCardContent = ({ label, description, warm, onClose }) => (
  <div
    className="rounded-2xl border p-4 sm:p-5 backdrop-blur-xl"
    style={{
      backgroundColor: 'rgba(8,15,30,0.92)',
      borderColor: 'rgba(96,165,250,0.3)',
      boxShadow: '0 8px 32px rgba(96,165,250,0.15), 0 0 0 1px rgba(96,165,250,0.08)',
    }}
  >
    <div className="flex items-start justify-between gap-2 mb-2">
      <h4 className="text-sm font-semibold tracking-wide" style={{ color: '#60a5fa', fontFamily: 'Inter,sans-serif' }}>{label}</h4>
      {onClose && (
        <button onClick={onClose} className="shrink-0 rounded-full p-0.5" style={{ color: 'rgba(96,165,250,0.6)' }}>
          <X size={14} />
        </button>
      )}
    </div>
    <p className="text-xs leading-relaxed" style={{ color: 'rgba(245,241,232,0.78)', fontFamily: 'Inter,sans-serif' }}>{description}</p>
  </div>
);

/* ── DNA overlay ── */
const DNAOverlay = () => (
  <div className="absolute inset-0 pointer-events-none overflow-hidden" style={{ opacity: 0.15, mixBlendMode: 'screen' }}>
    {[...Array(6)].map((_, i) => (
      <div key={i} className="absolute rounded-full" style={{
        width: 3, height: 3, backgroundColor: '#60a5fa',
        left: `${60 + Math.sin(i * 1.2) * 15}%`, top: `${15 + i * 14}%`,
        boxShadow: '0 0 8px rgba(96,165,250,0.6)',
        animation: `dnaFloat ${2 + i * 0.3}s ease-in-out infinite alternate`,
        animationDelay: `${i * 0.4}s`,
      }} />
    ))}
  </div>
);

/* ── SVG connection lines (inner → zone) ── */
const ConnectionLines = ({ marker, targets }) => (
  <svg className="absolute inset-0 w-full h-full pointer-events-none" style={{ opacity: 0.4, zIndex: 5 }}>
    {targets.map(t => (
      <line key={t.id}
        x1={`${marker.x}%`} y1={`${marker.y}%`} x2={`${t.x}%`} y2={`${t.y}%`}
        stroke="rgba(96,165,250,0.5)" strokeWidth="1" strokeDasharray="4 4"
        style={{ animation: 'dashMove 1.5s linear infinite' }}
      />
    ))}
  </svg>
);

/* ═══════════════════════════════════════════
   MAIN COMPONENT
   ═══════════════════════════════════════════ */
export const AgingExplorer = () => {
  const { t } = useTranslation();
  const isMobile = useIsMobile();
  const isTablet = useIsMobile(1024);
  const [mode, setMode] = useState('visible');
  const [activeZone, setActiveZone] = useState(null);
  const [activeMarker, setActiveMarker] = useState(null);
  const [sliderPos, setSliderPos] = useState(50);
  const [revealed, setRevealed] = useState(false);
  const containerRef = useRef(null);
  const imageRef = useRef(null);
  const isDragging = useRef(false);
  const hasInteracted = useRef(false);
  const sliderAnimated = useRef(false);

  const isVisible = mode === 'visible';

  /* ── Slider intro animation (triggered by reveal button) ── */
  const runSliderAnimation = useCallback(() => {
    if (sliderAnimated.current) return;
    sliderAnimated.current = true;

    const animateTo = (from, to, duration) => new Promise((resolve) => {
      const t0 = performance.now();
      const step = (now) => {
        const progress = Math.min((now - t0) / duration, 1);
        const eased = 1 - Math.pow(1 - progress, 3);
        setSliderPos(from + (to - from) * eased);
        if (progress < 1) requestAnimationFrame(step);
        else resolve();
      };
      requestAnimationFrame(step);
    });

    // Lock scroll
    document.documentElement.style.overflow = 'hidden';
    document.body.style.overflow = 'hidden';

    // Sequence: pause → 50→25 → pause → 25→75
    setTimeout(async () => {
      await animateTo(50, 25, 1800);
      await new Promise(r => setTimeout(r, 600));
      await animateTo(25, 75, 2200);
      // Unlock scroll after animation
      document.documentElement.style.overflow = '';
      document.body.style.overflow = '';
    }, 400);
  }, []);

  /* ── Handle reveal button click ── */
  const handleReveal = useCallback(() => {
    setRevealed(true);
    // Scroll section into comfortable view, then animate
    if (imageRef.current) {
      imageRef.current.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }
    setTimeout(() => runSliderAnimation(), 600);
  }, [runSliderAnimation]);

  /* ── Auto-cycle on first load (only after revealed) ── */
  useEffect(() => {
    if (!revealed || hasInteracted.current) return;
    let idx = 0;
    const spots = isVisible ? ZONE_SPOTS : INNER_SPOTS;
    const timer = setInterval(() => {
      if (hasInteracted.current) { clearInterval(timer); return; }
      const setter = isVisible ? setActiveZone : setActiveMarker;
      setter(spots[idx].id);
      idx = (idx + 1) % spots.length;
    }, 2500);
    return () => clearInterval(timer);
  }, [isVisible, revealed]);

  /* ── Slider drag ── */
  const getPos = useCallback((clientX) => {
    if (!imageRef.current) return 50;
    const rect = imageRef.current.getBoundingClientRect();
    return Math.min(Math.max(((clientX - rect.left) / rect.width) * 100, 2), 98);
  }, []);

  const onDragStart = useCallback((e) => {
    e.preventDefault();
    isDragging.current = true;
    setSliderPos(getPos(e.touches ? e.touches[0].clientX : e.clientX));
  }, [getPos]);

  const onDragMove = useCallback((e) => {
    if (!isDragging.current) return;
    setSliderPos(getPos(e.touches ? e.touches[0].clientX : e.clientX));
  }, [getPos]);

  const onDragEnd = useCallback(() => { isDragging.current = false; }, []);

  useEffect(() => {
    const opts = { passive: false };
    window.addEventListener('mousemove', onDragMove);
    window.addEventListener('mouseup', onDragEnd);
    window.addEventListener('touchmove', onDragMove, opts);
    window.addEventListener('touchend', onDragEnd);
    return () => {
      window.removeEventListener('mousemove', onDragMove);
      window.removeEventListener('mouseup', onDragEnd);
      window.removeEventListener('touchmove', onDragMove);
      window.removeEventListener('touchend', onDragEnd);
    };
  }, [onDragMove, onDragEnd]);

  const handleZoneSelect = useCallback((id) => {
    const wasAuto = !hasInteracted.current;
    hasInteracted.current = true;
    // If transitioning from auto-cycle, always select (don't toggle off)
    setActiveZone(prev => (wasAuto || prev !== id) ? id : null);
    setActiveMarker(null);
  }, []);

  const handleMarkerSelect = useCallback((id) => {
    const wasAuto = !hasInteracted.current;
    hasInteracted.current = true;
    setActiveMarker(prev => (wasAuto || prev !== id) ? id : null);
    setActiveZone(null);
  }, []);

  const closeCard = useCallback(() => {
    setActiveZone(null);
    setActiveMarker(null);
  }, []);

  // Glow data
  const activeZoneData = activeZone ? ZONE_SPOTS.find(z => z.id === activeZone) : null;
  const activeISpot = activeMarker ? INNER_SPOTS.find(s => s.id === activeMarker) : null;
  const highlightedZones = activeMarker
    ? INNER_SPOTS.find(m => m.id === activeMarker)?.relatedAreas || []
    : [];

  return (
    <section
      data-testid="aging-explorer-section"
      id="aging-explorer"
      className="py-16 lg:py-24 px-4 sm:px-6"
      style={{ backgroundColor: 'var(--vc-bg-2)' }}
    >
      <div className="max-w-[1160px] mx-auto">
        {/* Header */}
        <div className="text-center mb-8 sm:mb-10">
          <h2 className="font-bold tracking-tight text-2xl sm:text-3xl lg:text-[34px]" style={{ fontFamily: 'Inter,sans-serif', color: 'var(--vc-fg)' }}>
            {t('aging.title')}
          </h2>
          <p className="mt-3 text-sm sm:text-base leading-relaxed max-w-lg mx-auto" style={{ color: 'var(--vc-fg-muted)', fontFamily: 'Inter,sans-serif' }}>
            {t('aging.subtitle')}
          </p>
        </div>

        {/* Mode toggle — only after reveal */}
        {revealed && (
        <div className="flex justify-center mb-8 sm:mb-10" style={{ animation: 'ageCardIn 0.5s ease-out forwards' }}>
          <div className="inline-flex rounded-full p-1 gap-1" style={{ backgroundColor: 'rgba(255,255,255,0.06)', border: '1px solid rgba(255,255,255,0.1)' }}>
            <button
              data-testid="aging-toggle-visible"
              onClick={() => { setMode('visible'); closeCard(); }}
              className="relative px-5 sm:px-7 py-2.5 rounded-full text-xs sm:text-sm font-medium tracking-wide transition-all duration-300"
              style={{
                fontFamily: 'Inter,sans-serif',
                color: isVisible ? '#0a1220' : 'var(--vc-fg-muted)',
                backgroundColor: isVisible ? '#d6a84a' : 'transparent',
                boxShadow: isVisible ? '0 4px 16px rgba(214,168,74,0.35)' : 'none',
              }}
            >{t('aging.toggle_visible')}</button>
            <button
              data-testid="aging-toggle-inner"
              onClick={() => { setMode('inner'); closeCard(); }}
              className="relative px-5 sm:px-7 py-2.5 rounded-full text-xs sm:text-sm font-medium tracking-wide transition-all duration-300"
              style={{
                fontFamily: 'Inter,sans-serif',
                color: !isVisible ? '#0a1220' : 'var(--vc-fg-muted)',
                backgroundColor: !isVisible ? '#60a5fa' : 'transparent',
                boxShadow: !isVisible ? '0 4px 16px rgba(96,165,250,0.35)' : 'none',
              }}
            >{t('aging.toggle_inner')}</button>
          </div>
        </div>
        )}

        {/* Main content */}
        <div ref={containerRef} className="mx-auto" style={{ maxWidth: isMobile ? 400 : isTablet ? 520 : 600 }}>

          {/* Image + slider */}
          <div
            ref={imageRef}
            className="relative rounded-2xl sm:rounded-3xl select-none"
            style={{
              border: `1px solid ${isVisible ? 'rgba(214,168,74,0.2)' : 'rgba(96,165,250,0.2)'}`,
              boxShadow: isVisible
                ? '0 20px 60px rgba(214,168,74,0.08), 0 0 0 1px rgba(214,168,74,0.05)'
                : '0 20px 60px rgba(96,165,250,0.08), 0 0 0 1px rgba(96,165,250,0.05)',
              transition: 'border-color 0.5s, box-shadow 0.5s',
              overflow: 'hidden',
              touchAction: 'pan-y',
            }}
            onContextMenu={(e) => e.preventDefault()}
            onDragStart={(e) => e.preventDefault()}
          >
            {/* Young face (base) — CSS background, no <img> to save */}
            <div
              role="img"
              aria-label={t('aging.image_alt')}
              className="w-full"
              style={{
                backgroundImage: `url(${YOUNG_IMG})`,
                backgroundSize: 'cover',
                backgroundPosition: 'center',
                aspectRatio: '3 / 2',
                pointerEvents: 'none',
              }}
            />
            {/* Aging face (clipped) — CSS background */}
            <div
              className="absolute inset-0"
              style={{
                backgroundImage: `url(${AGING_IMG})`,
                backgroundSize: 'cover',
                backgroundPosition: 'center',
                clipPath: `inset(0 ${100 - sliderPos}% 0 0)`,
                pointerEvents: 'none',
              }}
            />

            {/* Tint overlays */}
            {!isVisible && <DNAOverlay />}
            <div className="absolute inset-0 pointer-events-none transition-opacity duration-700"
              style={{ background: 'linear-gradient(135deg, transparent 40%, rgba(30,58,138,0.15) 100%)', opacity: !isVisible ? 1 : 0 }} />
            <div className="absolute inset-0 pointer-events-none transition-opacity duration-700"
              style={{ background: 'linear-gradient(135deg, rgba(120,80,20,0.06) 0%, transparent 60%)', opacity: isVisible ? 1 : 0 }} />

            {/* Glow ring on active zone */}
            {isVisible && activeZoneData && (
              <GlowRing x={activeZoneData.x} y={activeZoneData.y} warm isMobile={isMobile} />
            )}

            {/* Glow rings on highlighted zones (inner mode) */}
            {!isVisible && highlightedZones.map(zId => {
              const z = ZONE_SPOTS.find(s => s.id === zId);
              if (!z) return null;
              return <GlowRing key={`hl-${z.id}`} x={z.x} y={z.y} warm={false} isMobile={isMobile} />;
            })}

            {/* Inner marker glow */}
            {!isVisible && activeISpot && (
              <GlowRing x={activeISpot.x} y={activeISpot.y} warm={false} isMobile={isMobile} />
            )}

            {/* Connection lines (inner → zones) */}
            {activeMarker && !isVisible && (() => {
              const mk = INNER_SPOTS.find(m => m.id === activeMarker);
              if (!mk) return null;
              const targets = ZONE_SPOTS.filter(z => mk.relatedAreas.includes(z.id));
              return <ConnectionLines marker={mk} targets={targets} />;
            })()}

            {/* Slider line + handle */}
            <div
              data-testid="aging-slider-line"
              className="absolute top-0 bottom-0 z-20"
              style={{ left: `${sliderPos}%`, transform: 'translateX(-50%)', width: 3, cursor: 'col-resize', opacity: revealed ? 1 : 0, transition: 'opacity 0.5s', pointerEvents: revealed ? 'auto' : 'none' }}
              onMouseDown={onDragStart} onTouchStart={onDragStart}
            >
              <div className="absolute inset-0" style={{
                background: 'linear-gradient(to bottom, transparent 0%, rgba(255,255,255,0.7) 10%, rgba(255,255,255,0.7) 90%, transparent 100%)',
                width: 2, left: '50%', transform: 'translateX(-50%)',
              }} />
              <div
                data-testid="aging-slider-handle"
                className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 flex items-center justify-center rounded-full"
                style={{
                  width: isMobile ? 44 : 40, height: isMobile ? 44 : 40,
                  backgroundColor: 'rgba(255,255,255,0.95)',
                  boxShadow: '0 2px 12px rgba(0,0,0,0.3), 0 0 0 2px rgba(255,255,255,0.4)',
                  cursor: 'col-resize',
                }}
              >
                <ChevronLeft size={14} style={{ color: '#333', marginRight: -2 }} />
                <ChevronRight size={14} style={{ color: '#333', marginLeft: -2 }} />
              </div>
            </div>

            {/* Edge labels */}
            <div className="absolute top-3 z-20 pointer-events-none flex justify-between w-full px-3 sm:px-4"
              style={{ opacity: revealed ? 1 : 0, transition: 'opacity 0.5s' }}>
              <span className="text-[10px] sm:text-xs font-medium tracking-wider uppercase px-2.5 py-1 rounded-full"
                style={{ fontFamily: 'Inter,sans-serif', backgroundColor: 'rgba(214,168,74,0.15)', color: '#d6a84a', border: '1px solid rgba(214,168,74,0.3)', backdropFilter: 'blur(12px)', opacity: sliderPos > 15 ? 1 : 0, transition: 'opacity 0.3s' }}>
                {t('aging.label_aging')}
              </span>
              <span className="text-[10px] sm:text-xs font-medium tracking-wider uppercase px-2.5 py-1 rounded-full"
                style={{ fontFamily: 'Inter,sans-serif', backgroundColor: 'rgba(96,165,250,0.15)', color: '#60a5fa', border: '1px solid rgba(96,165,250,0.3)', backdropFilter: 'blur(12px)', opacity: sliderPos < 85 ? 1 : 0, transition: 'opacity 0.3s' }}>
                {t('aging.label_youth')}
              </span>
            </div>

            {/* Mode badge */}
            <div className="absolute bottom-3 left-3 sm:bottom-4 sm:left-4 z-20 pointer-events-none"
              style={{ opacity: revealed ? 1 : 0, transition: 'opacity 0.5s' }}>
              <span className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-[10px] sm:text-xs font-medium tracking-wider uppercase"
                style={{
                  fontFamily: 'Inter,sans-serif',
                  backgroundColor: isVisible ? 'rgba(214,168,74,0.15)' : 'rgba(96,165,250,0.15)',
                  color: isVisible ? '#d6a84a' : '#60a5fa',
                  border: `1px solid ${isVisible ? 'rgba(214,168,74,0.3)' : 'rgba(96,165,250,0.3)'}`,
                  backdropFilter: 'blur(12px)', transition: 'all 0.4s ease',
                }}>
                <span className="w-1.5 h-1.5 rounded-full" style={{ backgroundColor: isVisible ? '#d6a84a' : '#60a5fa' }} />
                {isVisible ? t('aging.badge_visible') : t('aging.badge_inner')}
              </span>
            </div>

            {/* Drag hint */}
            <div className="absolute bottom-3 right-3 sm:bottom-4 sm:right-4 z-20 pointer-events-none"
              style={{ opacity: revealed ? 1 : 0, transition: 'opacity 0.5s' }}>
              <span className="text-[10px] sm:text-xs tracking-wide px-3 py-1 rounded-full"
                style={{ fontFamily: 'Inter,sans-serif', color: 'var(--vc-fg-subtle)', backgroundColor: 'rgba(7,21,37,0.7)', backdropFilter: 'blur(8px)', border: '1px solid rgba(255,255,255,0.08)' }}>
                {t('aging.drag_hint')}
              </span>
            </div>

            {/* ═══ FROSTED GLASS OVERLAY ═══ */}
            <div
              data-testid="aging-reveal-overlay"
              className="absolute inset-0 z-30 flex flex-col items-center justify-center"
              style={{
                backdropFilter: 'blur(18px) saturate(1.2)',
                WebkitBackdropFilter: 'blur(18px) saturate(1.2)',
                backgroundColor: 'rgba(7,21,37,0.55)',
                opacity: revealed ? 0 : 1,
                pointerEvents: revealed ? 'none' : 'auto',
                transition: 'opacity 1s ease-out',
              }}
            >
              <button
                data-testid="aging-reveal-button"
                onClick={handleReveal}
                className="group flex flex-col items-center gap-4 cursor-pointer"
                style={{ background: 'none', border: 'none' }}
              >
                {/* Pulsing ring */}
                <div className="relative flex items-center justify-center">
                  <div className="absolute w-16 h-16 rounded-full"
                    style={{
                      border: '1.5px solid rgba(214,168,74,0.3)',
                      animation: 'revealPulse 2.5s ease-in-out infinite',
                    }} />
                  <div className="w-14 h-14 rounded-full flex items-center justify-center"
                    style={{
                      backgroundColor: 'rgba(214,168,74,0.12)',
                      border: '1px solid rgba(214,168,74,0.4)',
                      transition: 'background-color 0.3s',
                    }}
                    onMouseEnter={(e) => e.currentTarget.style.backgroundColor = 'rgba(214,168,74,0.25)'}
                    onMouseLeave={(e) => e.currentTarget.style.backgroundColor = 'rgba(214,168,74,0.12)'}
                  >
                    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#d6a84a" strokeWidth="1.5">
                      <polygon points="5 3 19 12 5 21 5 3" fill="rgba(214,168,74,0.3)" stroke="#d6a84a" />
                    </svg>
                  </div>
                </div>
                <span
                  className="text-xs sm:text-sm font-medium tracking-[0.12em] uppercase"
                  style={{
                    color: 'rgba(214,168,74,0.9)',
                    fontFamily: 'Inter, sans-serif',
                    textShadow: '0 1px 8px rgba(0,0,0,0.5)',
                  }}
                >
                  {t('aging.reveal_button')}
                </span>
              </button>
            </div>
          </div>

          {/* ═══ TAB BAR (only after reveal) ═══ */}
          {revealed && isVisible && (
            <TabBar items={ZONE_SPOTS} activeId={activeZone} onSelect={handleZoneSelect} warm t={t} isMobile={isMobile} />
          )}
          {revealed && !isVisible && (
            <TabBar items={INNER_SPOTS} activeId={activeMarker} onSelect={handleMarkerSelect} warm={false} t={t} isMobile={isMobile} />
          )}

          {/* ═══ INFO CARD (below tabs) ═══ */}
          {isVisible && activeZoneData && (
            <div className="mt-3 mx-auto" style={{ maxWidth: 360, animation: 'ageCardIn 0.25s ease-out forwards' }}>
              <ZoneCardContent zoneLabel={t(activeZoneData.labelKey)} subs={activeZoneData.subs} warm onClose={closeCard} t={t} />
            </div>
          )}
          {!isVisible && activeISpot && (
            <div className="mt-3 mx-auto" style={{ maxWidth: 360, animation: 'ageCardIn 0.25s ease-out forwards' }}>
              <InnerCardContent label={t(activeISpot.labelKey)} description={t(activeISpot.descKey)} warm={false} onClose={closeCard} />
            </div>
          )}
        </div>

        {/* Footer */}
        <p className="text-center mt-6 sm:mt-8 text-xs sm:text-sm max-w-md mx-auto leading-relaxed"
          style={{ color: 'var(--vc-fg-subtle)', fontFamily: 'Inter,sans-serif' }}>
          {t('aging.footer_note')}
        </p>
      </div>

      <style>{`
        @keyframes ageCardIn {
          from { opacity: 0; transform: translateY(6px); }
          to   { opacity: 1; transform: translateY(0); }
        }
        @keyframes glowAppear {
          from { opacity: 0; transform: translate(-50%,-50%) scale(0.6); }
          to   { opacity: 1; transform: translate(-50%,-50%) scale(1); }
        }
        @keyframes pulseGlow {
          0%, 100% { opacity: 0.7; transform: scale(1); }
          50%      { opacity: 1;   transform: scale(1.15); }
        }
        @keyframes breathe {
          0%, 100% { opacity: 0.5; transform: translate(-50%,-50%) scale(0.85); }
          50%      { opacity: 1;   transform: translate(-50%,-50%) scale(1.15); }
        }
        @keyframes dnaFloat {
          from { transform: translateY(0); }
          to   { transform: translateY(-8px); }
        }
        @keyframes dashMove {
          to { stroke-dashoffset: -16; }
        }
        @keyframes revealPulse {
          0%, 100% { opacity: 0.4; transform: scale(1); }
          50%      { opacity: 0.9; transform: scale(1.2); }
        }
        .no-scrollbar::-webkit-scrollbar { display: none; }
      `}</style>
    </section>
  );
};
